import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLocale } from "@/context/LocaleContext";
// import SetFavicon from "./SetFavicon"; // ❌ COMMENTATO - Causa problemi description
import { useState, useEffect } from "react";
import { Menu, X, LogOut } from "lucide-react";

export default function Header() {
  const navigate = useNavigate();
  const { t, lang, setLang } = useLocale();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if admin session is active
    const adminToken = localStorage.getItem("buildstore.admin.token");
    const adminExpiry = localStorage.getItem("buildstore.admin.expiry");

    if (adminToken && adminExpiry) {
      const expiryTime = parseInt(adminExpiry, 10);
      if (Date.now() < expiryTime) {
        setIsAdmin(true);
      }
    }
  }, [location]);

  const handleAdminLogout = () => {
    localStorage.removeItem("buildstore.admin.token");
    localStorage.removeItem("buildstore.admin.expiry");
    localStorage.removeItem("buildstore.blog.access");
    setIsAdmin(false);
    setMenuOpen(false);
    navigate("/");
  };

  const handleLanguageSwitch = (newLang: "en" | "it") => {
    setLang(newLang);
    const currentPath = location.pathname;
    let newPath = currentPath;

    if (newLang === "en") {
      if (!currentPath.startsWith("/en")) {
        if (currentPath === "/") {
          newPath = "/en";
        } else if (currentPath === "/chi-siamo") {
          newPath = "/en/about";
        } else if (currentPath === "/valore") {
          newPath = "/en/value";
        } else if (currentPath === "/contattaci") {
          newPath = "/en/contact";
        } else if (currentPath.startsWith("/blog/")) {
          const slug = currentPath.split("/")[2];
          newPath = `/en/blog/${slug}`;
        } else if (currentPath.startsWith("/case-study/")) {
          const slug = currentPath.split("/")[2];
          newPath = `/en/case-study/${slug}`;
        } else if (currentPath.startsWith("/template/")) {
          const id = currentPath.split("/")[2];
          newPath = `/en/template/${id}`;
        } else {
          newPath = `/en${currentPath}`;
        }
      }
    } else {
      if (currentPath.startsWith("/en")) {
        const withoutLang = currentPath.slice(3) || "/";
        if (withoutLang === "/about") {
          newPath = "/chi-siamo";
        } else if (withoutLang === "/value") {
          newPath = "/valore";
        } else if (withoutLang === "/contact") {
          newPath = "/contattaci";
        } else if (withoutLang.startsWith("/blog/")) {
          const slug = withoutLang.split("/")[2];
          newPath = `/blog/${slug}`;
        } else if (withoutLang.startsWith("/case-study/")) {
          const slug = withoutLang.split("/")[2];
          newPath = `/case-study/${slug}`;
        } else if (withoutLang.startsWith("/template/")) {
          const id = withoutLang.split("/")[2];
          newPath = `/template/${id}`;
        } else {
          newPath = withoutLang;
        }
      }
    }

    navigate(newPath);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b border-white/10">
      {/* ❌ SetFavicon COMMENTATO - Sovrascrive la description e causa problemi SEO */}
      {/* <SetFavicon /> */}

      <div className="container mx-auto flex items-center justify-between py-12 px-4 relative">
        <div className="flex items-center gap-2" style={{ marginLeft: 4 }}>
          <button
            onClick={() => navigate("/")}
            aria-label={t("header.go_home_aria")}
            className="inline-flex h-16 w-16 sm:h-24 sm:w-24 md:h-28 md:w-28 lg:h-32 lg:w-32 p-0 m-0 rounded-full overflow-hidden focus:outline-none flex-shrink-0 leading-none"
            title={t("header.homepage_title")}
            style={{ clipPath: "circle(50% at 50% 50%)" }}
          >
            <img
              src="https://cdn.builder.io/api/v1/image/assets%2F1abb0f3b5e864cc1bc1463ed28f1bd4f%2F8bb3efc30e5540199940bb13e66408b1?format=webp&width=256"
              alt="BuildStore logo"
              className="h-full w-full object-cover"
            />
          </button>
          <Link
            to="/"
            className="ml-1 brand-font text-sm sm:text-lg md:text-xl lg:text-2xl font-semibold brand-letterspace-tight text-primary drop-shadow-[0_0_12px_rgba(16,185,129,0.35)]"
          >
            <div className="flex flex-col items-start">
              <h2 className="m-0 text-xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight">
                BuildStore
              </h2>
              <h4 className="m-0 text-xs sm:text-sm text-white/70 font-medium mt-0">
                {t("header.subtitle")}
              </h4>
            </div>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-4">
          <Link
            to="/products"
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
              location.pathname === "/products"
                ? "bg-primary text-primary-foreground shadow-[0_0_16px_rgba(16,185,129,0.3)]"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            🎯 {t("nav.projects")}
          </Link>
          <Link
            to="/pricing"
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
              location.pathname === "/pricing"
                ? "bg-primary text-primary-foreground shadow-[0_0_16px_rgba(16,185,129,0.3)]"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            💰 {t("nav.pricing")}
          </Link>
          <Link
            to="/blog"
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
              location.pathname === "/blog" ||
              location.pathname.startsWith("/blog/")
                ? "bg-primary text-primary-foreground shadow-[0_0_16px_rgba(16,185,129,0.3)]"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            📝 {t("nav.blog")}
          </Link>
          <Link
            to="/valore"
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
              location.pathname === "/valore"
                ? "bg-primary text-primary-foreground shadow-[0_0_16px_rgba(16,185,129,0.3)]"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            ⚡ {t("nav.value")}
          </Link>
          <Link
            to="/chi-siamo"
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
              location.pathname === "/chi-siamo"
                ? "bg-primary text-primary-foreground shadow-[0_0_16px_rgba(16,185,129,0.3)]"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            👥 {t("nav.about")}
          </Link>
          <Link
            to="/case-studies"
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
              location.pathname === "/case-studies"
                ? "bg-primary text-primary-foreground shadow-[0_0_16px_rgba(16,185,129,0.3)]"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            📚 {t("nav.case_studies")}
          </Link>
          <Link
            to="/contattaci"
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
              location.pathname === "/contattaci"
                ? "bg-primary text-primary-foreground shadow-[0_0_16px_rgba(16,185,129,0.3)]"
                : "text-white/70 hover:text-white hover:bg-white/10"
            }`}
          >
            📞 {t("nav.contact")}
          </Link>
        </nav>

        {/* Language Switcher - All Pages - Top Right Corner */}
        <div className="absolute right-4 top-6 z-50 flex items-center gap-2">
          <button
            onClick={() => handleLanguageSwitch("en")}
            aria-label={t("country.en") || "English"}
            className={`px-2 py-1 rounded transition-all ${lang === "en" ? "ring-2 ring-primary" : ""}`}
            title="English"
          >
            <svg
              width="22"
              height="14"
              viewBox="0 0 60 30"
              aria-hidden="true"
              focusable="false"
            >
              <rect width="60" height="30" fill="#012169" />
              <g stroke="#fff" strokeWidth={6} strokeLinecap="butt" fill="none">
                <path d="M0 0 L60 30" />
                <path d="M60 0 L0 30" />
              </g>
              <g
                stroke="#C8102E"
                strokeWidth={4}
                strokeLinecap="butt"
                fill="none"
              >
                <path d="M0 0 L60 30" />
                <path d="M60 0 L0 30" />
              </g>
              <rect x={25} y={0} width={10} height={30} fill="#fff" />
              <rect x={0} y={10} width={60} height={10} fill="#fff" />
              <rect x={27} y={0} width={6} height={30} fill="#C8102E" />
              <rect x={0} y={11} width={60} height={8} fill="#C8102E" />
            </svg>
          </button>

          <button
            onClick={() => handleLanguageSwitch("it")}
            aria-label={t("country.it") || "Italiano"}
            className={`px-2 py-1 rounded transition-all ${lang === "it" ? "ring-2 ring-primary" : ""}`}
            title="Italiano"
          >
            <svg
              width="20"
              height="14"
              viewBox="0 0 20 14"
              aria-hidden="true"
              focusable="false"
            >
              <rect width="6.666" height="14" x="0" fill="#009246" />
              <rect width="6.666" height="14" x="6.667" fill="#ffffff" />
              <rect width="6.666" height="14" x="13.333" fill="#ce2b37" />
            </svg>
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors ml-auto mr-2"
          aria-label="Toggle menu"
        >
          {menuOpen ? (
            <X size={24} className="text-white" />
          ) : (
            <Menu size={24} className="text-white" />
          )}
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      {menuOpen && (
        <nav className="md:hidden border-t border-white/10 bg-black/80 backdrop-blur-sm pt-16">
          <div className="container mx-auto px-4 pb-4 flex flex-col gap-2">
            <Link
              to="/products"
              onClick={() => setMenuOpen(false)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                location.pathname === "/products"
                  ? "bg-primary text-primary-foreground"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
            >
              🎯 {t("nav.projects")}
            </Link>
            <Link
              to="/pricing"
              onClick={() => setMenuOpen(false)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                location.pathname === "/pricing"
                  ? "bg-primary text-primary-foreground"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
            >
              💰 {t("nav.pricing")}
            </Link>
            <Link
              to="/blog"
              onClick={() => setMenuOpen(false)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                location.pathname === "/blog" ||
                location.pathname.startsWith("/blog/")
                  ? "bg-primary text-primary-foreground"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
            >
              📝 {t("nav.blog")}
            </Link>
            <Link
              to="/valore"
              onClick={() => setMenuOpen(false)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                location.pathname === "/valore"
                  ? "bg-primary text-primary-foreground"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
            >
              ⚡ {t("nav.value")}
            </Link>
            <Link
              to="/chi-siamo"
              onClick={() => setMenuOpen(false)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                location.pathname === "/chi-siamo"
                  ? "bg-primary text-primary-foreground"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
            >
              👥 {t("nav.about")}
            </Link>
            <Link
              to="/case-studies"
              onClick={() => setMenuOpen(false)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                location.pathname === "/case-studies"
                  ? "bg-primary text-primary-foreground"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
            >
              📚 {t("nav.case_studies")}
            </Link>
            <Link
              to="/contattaci"
              onClick={() => setMenuOpen(false)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                location.pathname === "/contattaci"
                  ? "bg-primary text-primary-foreground"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
            >
              📞 {t("nav.contact")}
            </Link>
            <div className="border-t border-white/10 pt-2 mt-2">
              {isAdmin ? (
                <button
                  onClick={handleAdminLogout}
                  className="w-full text-left px-4 py-2 rounded-lg font-medium transition-all duration-200 text-red-400 hover:text-red-300 hover:bg-red-500/10 flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Admin Logout
                </button>
              ) : (
                <Link
                  to="/admin-login"
                  onClick={() => setMenuOpen(false)}
                  className="block px-4 py-2 rounded-lg font-medium transition-all duration-200 text-primary hover:bg-primary/10"
                >
                  🔐 Admin Access
                </Link>
              )}
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
